import axios from 'axios';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import isEmail from 'validator/lib/isEmail.js';

import { captureException } from '@sentry/react';

import type { PasswordResetInitBodyT } from 'server/controllers';
import { getFirebaseClientService } from 'services';

import {
  P_API_AUTH_PASSWORD_RESET_INIT,
  getConfig,
  useGlobalState,
} from 'utils';

type UseLoginOptionsT = {
  target?: string;
};

type UseLoginResT = {
  busy: boolean;
  errorMessage: string;
  login: (remember?: boolean) => Promise<void>;
  password: string;
  passwordOk: boolean;
  resetPasswordV2: (app: 'admin' | 'aiapp') => Promise<void>;
  setErrorMessage: (message: string) => void;
  setPassword: (value: string) => void;
  setUsername: (value: string) => void;
  username: string;
  usernameOk: boolean;
};

export default function useLogin(options: UseLoginOptionsT = {}): UseLoginResT {
  const [busy, setBusy] = useState(false);

  const [query] = useSearchParams();

  const [errorMessage, setErrorMessage] = useState('');
  const [username, setUsername] = useState(query.get('email') ?? '');
  const [password, setPassword] = useState('');

  const [/* user */, setUser] = useGlobalState('user');

  const passwordOk = !!password;
  const usernameOk = isEmail(username);

  const navigate = useNavigate();

  return {
    busy,
    errorMessage,

    async login(remember?: boolean) {
      if (!usernameOk || !passwordOk) {
        throw Error('Log in trigger with invalid initial state');
      }

      try {
        setBusy(true);

        const {
          passesAppCheck,
          signInWithEmailAndPassword,
        } = await getFirebaseClientService();

        const isHuman = await passesAppCheck();
        if (!isHuman) {
          throw Error('Failed to register at this time, try again later.');
        }

        const user = await signInWithEmailAndPassword(
          username,
          password,
          remember,
        );

        setUser(user);
        setErrorMessage('');
        void navigate(options.target ?? '/');
      } catch (error: unknown) {
        // TODO: If error.code is "auth/email-already-in-use" we should add
        // the password reset link to the error message!

        const errorCode: string = error && typeof error === 'object'
          && 'code' in error ? error.code as string : 'internal-error';

        switch (errorCode) {
          case 'auth/user-not-found':
          case 'auth/wrong-password':
            setErrorMessage('Invalid credentials');
            break;
          default:
            setErrorMessage(errorCode);
        }
      } finally {
        setBusy(false);
      }
    },

    password,
    passwordOk,

    async resetPasswordV2(app) {
      try {
        const config = getConfig(true);
        await axios.post(P_API_AUTH_PASSWORD_RESET_INIT, {
          _csrf: config.CSRF,
          app,
          email: username,
        } satisfies PasswordResetInitBodyT);
      } catch (error) {
        captureException(error);
        // eslint-disable-next-line no-alert
        alert('Password reset failed');
      }
    },

    setErrorMessage,
    setPassword,
    setUsername,
    username,
    usernameOk,
  };
}
